/**
 * MyProgress (Sprint 16 B2) — Adaptive Learning Path page.
 *
 * Pillar P2 foundation UI. Fetch /api/learning/recommendations/me/ và render:
 *   - Strong topics: bar chart hiển thị lesson user familiar (citation count)
 *   - Weak topics: list lesson chưa chat → CTA "Học bài này" link đến lesson
 *
 * Sprint 17+ có thể upgrade: filter by course, velocity decay, skill graph
 * visualization, course-level recommendation.
 */

import { Alert, Card, Col, Empty, Row, Space, Spin, Tag, Tooltip as AntTooltip, Typography } from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Link } from "react-router-dom";
import {
  ArrowRightOutlined,
  BookOutlined,
  BulbOutlined,
  FireOutlined,
  RocketOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useMyRecommendations } from "@/api/learning";

const { Title, Text, Paragraph } = Typography;

export function MyProgress() {
  const { data, isLoading, error } = useMyRecommendations();

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }} data-testid="progress-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Không tải được tiến độ học"
        description="Vui lòng thử lại sau."
        showIcon
      />
    );
  }

  const strong = data?.strong_topics ?? [];
  const weak = data?.weak_topics ?? [];
  const suggestedCourses = data?.suggested_courses ?? [];
  // Sprint 20 B1 — backend flag bật velocity decay (half-life 30d).
  // Khi true: citation_count là float weighted, FE render decay badge.
  const decayEnabled = data?.decay_enabled ?? false;

  // Chart data: strong topics sorted desc by citation_count
  const chartData = strong.map((t) => ({
    title: t.lesson_title.length > 30 ? t.lesson_title.slice(0, 30) + "..." : t.lesson_title,
    count: t.citation_count ?? 0,
  }));

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }} data-testid="my-progress">
      <Title level={2}>
        <BulbOutlined /> Tiến độ học của bạn
      </Title>
      <Paragraph type="secondary">
        Hệ thống AI Tutor phân tích chat history để đề xuất bài học bạn nên ôn thêm.
      </Paragraph>

      <Row gutter={[16, 16]}>
        {/* ─── Strong topics chart ─────────────────────────────── */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <Space size={8}>
                <span>
                  <FireOutlined style={{ color: "#fa8c16" }} /> Chủ đề bạn nắm vững
                </span>
                {decayEnabled && (
                  <AntTooltip title="Trọng số ưu tiên hoạt động gần đây (giảm 50% sau mỗi 30 ngày). Bài chat trong tháng được tính 100%, 2 tháng trước còn 25%.">
                    <Tag
                      icon={<ThunderboltOutlined />}
                      color="purple"
                      data-testid="decay-badge"
                      style={{ cursor: "help", fontSize: 11 }}
                    >
                      Hoạt động gần đây
                    </Tag>
                  </AntTooltip>
                )}
              </Space>
            }
            data-testid="strong-topics-card"
          >
            {strong.length === 0 ? (
              <Empty
                description="Chưa có dữ liệu. Hãy chat với AI Tutor về các bài học để hệ thống học sở thích của bạn."
                style={{ padding: 32 }}
              />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 10, right: 16, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="title"
                    angle={-25}
                    textAnchor="end"
                    height={70}
                    interval={0}
                    style={{ fontSize: 11 }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Lần trích dẫn" fill="#fa8c16" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        {/* ─── Suggested courses card (Sprint 17 B1) ──────────── */}
        {suggestedCourses.length > 0 && (
          <Col xs={24}>
            <Card
              title={
                <span>
                  <RocketOutlined style={{ color: "#52c41a" }} /> Bắt đầu môn học mới
                </span>
              }
              data-testid="suggested-courses-card"
              extra={<Tag color="green">{suggestedCourses.length} khóa</Tag>}
            >
              <Space wrap size={[12, 12]}>
                {suggestedCourses.slice(0, 6).map((c) => (
                  <Link key={c.course_id} to={`/courses/${c.course_slug}`}>
                    <Tag
                      icon={<BookOutlined />}
                      color="green"
                      style={{ padding: "6px 12px", fontSize: 13, cursor: "pointer" }}
                    >
                      <strong>{c.course_code}</strong> — {c.course_name}
                    </Tag>
                  </Link>
                ))}
              </Space>
            </Card>
          </Col>
        )}

        {/* ─── Weak topics suggest list ────────────────────────── */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <span>
                <BulbOutlined style={{ color: "#1677ff" }} /> Bài học nên ôn
              </span>
            }
            data-testid="weak-topics-card"
            extra={<Tag color="blue">{weak.length} bài</Tag>}
          >
            {weak.length === 0 ? (
              <Empty
                description="Bạn đã chat đủ với AI Tutor về tất cả bài đã enroll. Tuyệt vời!"
                style={{ padding: 32 }}
              />
            ) : (
              <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                {weak.slice(0, 10).map((t) => (
                  <li
                    key={t.lesson_id}
                    style={{ padding: "6px 0", borderBottom: "1px dashed #f0f0f0" }}
                  >
                    <Link to={`/lessons/${t.lesson_id}`}>
                      <Text>{t.lesson_title}</Text>{" "}
                      <ArrowRightOutlined style={{ fontSize: 11, color: "#1677ff" }} />
                    </Link>
                  </li>
                ))}
                {weak.length > 10 && (
                  <li style={{ padding: "8px 0", color: "#999" }}>
                    + {weak.length - 10} bài khác
                  </li>
                )}
              </ul>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default MyProgress;
